/* eslint-disable jsx-a11y/label-has-associated-control */
import { Button, Dialog, IconButton } from '@material-ui/core';
import { XIcon } from '@phosphor-icons/react';
import { FC, useEffect, useState } from 'react';
import SearchAutocomplete from '../../shared/components/SearchAutocomplete';
import { ConsultantSummary } from '../../shared/types';
import { hasMultipleStaffRoleGroups, staffRoleGroupBy } from '../../shared/utils';
import { BORDER, createAdminPageStyles, MUTED, NAVY, TEXT } from '../../style';

interface ChangeConsultantModalProps {
  open: boolean;
  consultants: ConsultantSummary[];
  consultantId: number | '';
  onClose: () => void;
  onConfirm: (id: number) => void;
}

const consultantLabel = (consultant: ConsultantSummary) =>
  `${consultant.firstname || ''} ${consultant.lastname || ''}`.trim();

const useStyles = createAdminPageStyles({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 560,
      width: '100%',
      margin: 16,
      padding: '28px 32px 24px',
      boxShadow: '0 16px 40px rgba(14, 18, 71, 0.18)',
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontSize: 20,
    fontWeight: 600,
    color: '#000B33',
    margin: 0,
    lineHeight: 1.3,
  },
  dialogClose: {
    margin: '-8px -8px 0 0',
    color: TEXT,
    padding: 6,
  },
  field: {
    marginBottom: 24,
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  cancelBtn: {
    background: '#FFFFFF',
    color: NAVY,
    border: `1.5px solid ${BORDER}`,
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: 'none',
    '&:hover': {
      background: '#F7F8FB',
      boxShadow: 'none',
    },
  },
  confirmBtn: {
    background: '#000A8D',
    color: '#FFFFFF',
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: 'none',
    '&:hover': {
      background: '#1b2158',
      boxShadow: 'none',
    },
    '&:disabled': {
      background: '#E8EAF0',
      color: MUTED,
    },
  },
});

const ChangeConsultantModal: FC<ChangeConsultantModalProps> = ({
  open,
  consultants,
  consultantId,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [draftId, setDraftId] = useState<number | ''>(consultantId);
  const groupConsultants = hasMultipleStaffRoleGroups(consultants);

  useEffect(() => {
    if (open) {
      setDraftId(consultantId);
    }
  }, [open, consultantId]);

  const selectedConsultant =
    consultants.find(item => Number(item.id) === Number(draftId)) || null;

  const handleConfirm = () => {
    if (!draftId) {
      return;
    }
    onConfirm(Number(draftId));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      aria-labelledby="change-consultant-title">
      <div className={classes.dialogHeader}>
        <h2 id="change-consultant-title" className={classes.dialogTitle}>
          Changer le consultant
        </h2>
        <IconButton aria-label="Fermer" className={classes.dialogClose} onClick={onClose}>
          <XIcon size={20} />
        </IconButton>
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="change-consultant-select">
          Consultant rattaché à cette prestation
        </label>
        <SearchAutocomplete
          id="change-consultant-select"
          options={consultants}
          value={selectedConsultant}
          placeholder="Rechercher un nom ou un prénom"
          getOptionLabel={consultantLabel}
          groupBy={groupConsultants ? staffRoleGroupBy : undefined}
          onChange={selected => {
            const consultant = selected as ConsultantSummary | null;
            setDraftId(consultant ? Number(consultant.id) : '');
          }}
        />
      </div>

      <div className={classes.actions}>
        <Button className={classes.cancelBtn} onClick={onClose}>
          Annuler
        </Button>
        <Button className={classes.confirmBtn} onClick={handleConfirm} disabled={!draftId}>
          Confirmer le changement
        </Button>
      </div>
    </Dialog>
  );
};

export default ChangeConsultantModal;
